import React, { useState, useRef, useEffect } from 'react';
import { Form, Button, Dropdown } from 'react-bootstrap';
import { translateComponent } from '@/utils/tolgee/translateComponent';
import { LomavisBadge1 } from '@/lomavisSharedUi/atoms/badges/LomavisBadge1';
import { lomavisShowToastr } from '@/utils/ui/toastr';

export interface TagOption {
  uuid: string;
  name: string;
  color?: string;
  created?: string;
  modified?: string;
}

interface LomavisTagSelector1Props {
  /** The list of currently selected tags */
  selectedTags: TagOption[];
  /** The list of available tags to choose from */
  availableTags: TagOption[];
  /** Callback function when tags are changed */
  onTagsChange: (tags: TagOption[]) => void;
  /** Function to create new tags - called when user creates a new tag */
  onCreateTag?: (tagName: string) => Promise<TagOption> | TagOption;

  /** Function to delete tags from available tags */
  deleteTag?: (id: string) => void;

  /** Custom CSS classes */
  className?: string;
  /** Whether to allow creating new tags */
  allowCreate?: boolean;

  /** Customizable text props */
  addTagsText?: string;
  createNewTagsText?: string;
  availableTagsText?: string;
  noTagsAvailableText?: string;
  allTagsSelected?: string;

  maxAllowedTags?: number;
  loadPost?: () => void;
}

/**
 * Molecules: LomavisTagSelector1
 */

export const LomavisTagSelector1: React.FC<LomavisTagSelector1Props> = ({
  selectedTags = [],
  availableTags = [],
  onTagsChange,
  onCreateTag,
  className = '',
  allowCreate = true,
  deleteTag,
  addTagsText,
  createNewTagsText,
  availableTagsText,
  noTagsAvailableText,
  allTagsSelected,
  maxAllowedTags,
  loadPost
}) => {
  const t = translateComponent('LomavisTagSelector1');
  const [showDropdown, setShowDropdown] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // 👇 NEW: ref for dropdown container
  const dropdownRef = useRef<HTMLDivElement>(null);

  const availableUnselectedTags = availableTags.filter(
    (tag) => !selectedTags.find((selected) => selected.uuid === tag.uuid)
  );

  const handleTagToggle = (tag: TagOption) => {
    const isSelected = selectedTags.find(
      (selected) => selected.uuid === tag.uuid
    );
    if (isSelected) {
      onTagsChange(
        selectedTags.filter((selected) => selected.uuid !== tag.uuid)
      );
    } else {
      // 👇 block adding more if limit reached
      if (maxAllowedTags && selectedTags.length >= maxAllowedTags) {
        lomavisShowToastr(t('maxSelectedTagsExceeded'), 'error');
        return;
      }

      onTagsChange([...selectedTags, tag]);
    }
  };

  const handleCreateTag = async () => {
    if (!newTagName.trim() || isCreating) return;

    // Check if tag already exists
    if (
      availableTags.find(
        (tag) => tag.name.toLowerCase() === newTagName.toLowerCase()
      )
    ) {
      return;
    }

    setIsCreating(true);
    try {
      let newTag: TagOption;

      if (onCreateTag) {
        // Make sure we fully await the result (sync or async)
        const created = await Promise.resolve(onCreateTag(newTagName.trim()));

        // If parent signals failure by returning null/undefined, bail out
        if (!created) {
          setNewTagName('');
          return;
        }

        newTag = created;
      } else {
        // Default creation path if no onCreateTag is provided
        newTag = {
          uuid: `new-${Date.now()}`,
          name: newTagName.trim()
        };
      }

      onTagsChange([...selectedTags, newTag]);
      setNewTagName('');
    } catch (error) {
      console.error('Failed to create tag:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && newTagName.trim()) {
      e.preventDefault();
      void handleCreateTag();
    }
  };

  // 👇 NEW: close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
        loadPost?.();
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  // text fallbacks
  const addText = addTagsText || t('addTags');
  const createText = createNewTagsText || t('create_new_tags');
  const availableText = availableTagsText || t('availableTags');
  const noTagsText = noTagsAvailableText || t('no_tags_available');
  const allTagsSelectedText = allTagsSelected || t('all_tags_selected');

  console.log('showDropdown', showDropdown);
  return (
    <div className={className} ref={dropdownRef}>
      <Dropdown show={showDropdown} autoClose={false}>
        <Dropdown.Toggle
          as={Button}
          size="sm"
          bsPrefix={selectedTags.length > 0 && 'btn'} // 👈 prevents .dropdown-toggle class (caret)
          variant="outline-secondary"
          className={`border-secondary d-flex align-items-center justify-content-center ${
            selectedTags.length > 0 ? 'square-btn p-4 ' : ''
          }`}
          style={{
            borderWidth: '2px',
            borderStyle: 'dashed',
            aspectRatio: selectedTags.length > 0 ? 1 : 'auto'
          }}
          onClick={() => setShowDropdown((prev) => !prev)}
        >
          {selectedTags.length > 0 ? (
            <i className="fas fa-plus p-0"></i>
          ) : (
            addText
          )}
        </Dropdown.Toggle>

        <Dropdown.Menu className="w-100" style={{ minWidth: '350px' }}>
          <div className="p-3">
            {/* Create new tag input */}
            {allowCreate && (
              <div className="mb-3">
                <div className="d-flex gap-2">
                  <Form.Control
                    ref={inputRef}
                    type="text"
                    size="sm"
                    placeholder={createText}
                    value={newTagName}
                    onChange={(e) => setNewTagName(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={isCreating}
                    maxLength={20}
                  />
                  <Button
                    variant="primary"
                    size="sm"
                    // eslint-disable-next-line no-void
                    onClick={() => void handleCreateTag()}
                    disabled={!newTagName.trim() || isCreating}
                    className="cursor-pointer p-4 square-btn d-flex align-items-center justify-content-center"
                    style={{
                      aspectRatio: selectedTags.length > 0 ? 1 : 'auto'
                    }}
                  >
                    {isCreating ? (
                      <i className="fas fa-spinner fa-spin p-0"></i>
                    ) : (
                      <i className="fas fa-plus p-0"></i>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Available tags */}
            {availableUnselectedTags.length > 0 && (
              <div>
                <h6 className="fw-medium mb-2">
                  <i className="fas fa-tags me-2"></i>
                  {availableText}
                </h6>
                <div style={{ maxHeight: '160px', overflowY: 'auto' }}>
                  {availableUnselectedTags.map((tag) => (
                    <div
                      key={tag.uuid}
                      className="d-flex justify-content-between align-items-center p-2 rounded cursor-pointer hover-bg-light"
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleTagToggle(tag)}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = '#f8f9fa')
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor = 'transparent')
                      }
                    >
                      <LomavisBadge1
                        id={tag.uuid}
                        color={tag.color}
                        title={tag.name}
                      />

                      {deleteTag && (
                        <i
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteTag(tag.uuid);
                          }}
                          className="fa-duotone fa-trash cursor-pointer px-2"
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {availableUnselectedTags.length === 0 &&
              selectedTags?.length === 0 &&
              !newTagName && (
                <div className="text-center text-muted py-4">
                  <i className="fas fa-tag fa-2x mb-2"></i>
                  <div>{noTagsText}</div>
                </div>
              )}

            {availableUnselectedTags.length === 0 &&
              selectedTags?.length > 0 &&
              !newTagName && (
                <div className="text-center text-muted py-4">
                  <i className="fas fa-tag fa-2x mb-2"></i>
                  <div>{allTagsSelectedText}</div>
                </div>
              )}

            {loadPost && (
              <div className="w-100 d-flex justify-content-center align-items-center">
                <Button
                  size="sm"
                  variant="primary"
                  disabled={selectedTags?.length === 0}
                  onClick={() => {
                    loadPost();
                    setShowDropdown(false);
                  }}
                  className="d-flex justify-content-center align-items-center gap-2"
                >
                  {t('loadData')}
                </Button>
              </div>
            )}
          </div>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};
